import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, Star, Sparkles, Tag, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import api from '../lib/api'
import { toast } from '../lib/toast'
import { useAuthStore } from '../store/authStore'
import { useUiStore } from '../store/uiStore'
import { useConfirm } from '../lib/useConfirm'
import PaymentModal from '../components/PaymentModal'
import SocialButtons from '../components/SocialButtons'
import { currencyForLang, currencySymbol } from '../lib/currency'
import { useFocusTrap } from '../lib/useFocusTrap'
import { useSeoHead } from '../lib/seo'

interface VoucherState {
  code: string
  status: 'idle' | 'checking' | 'valid' | 'invalid'
  error?: string
  discount?: { type: string; value: number; discount_czk: number; discount_halere: number; final_price_czk: number; planId?: string | null }
}

export default function PricingPage() {
  const { t, i18n } = useTranslation()
  useSeoHead({
    title: t('pricing.meta_title', { defaultValue: 'Ceník — plány Starter, Pro a Enterprise | SEO Tools' }),
    description: t('pricing.meta_desc', { defaultValue: 'Sledování pozic na Googlu i Seznamu, crawl webu, GEO a AI optimalizace obsahu. Základní analýza zdarma, placené plány od 490 Kč měsíčně.' }),
    canonicalPath: '/pricing',
  })
  const cur = currencyForLang(i18n.language)
  const sym = currencySymbol(cur)
  const { isAuthenticated, user, loginWithToken, loadUser } = useAuthStore()
  const { openAuthModal } = useUiStore()
  // Guest checkout — host (bez přihlášení) zadá e-mail a nakoupí; účet se založí automaticky.
  const [guestPlan, setGuestPlan] = useState<string | null>(null)
  const [guestEmail, setGuestEmail] = useState('')
  const [guestErr, setGuestErr] = useState('')
  const [guestLoading, setGuestLoading] = useState(false)
  const guestModalRef = useRef<HTMLDivElement>(null)
  useFocusTrap(guestModalRef, () => setGuestPlan(null), !!guestPlan)
  const navigate = useNavigate()
  const { confirm, ConfirmDialog } = useConfirm()
  const [paymentData, setPaymentData] = useState<{
    clientSecret: string
    stripeKey: string
    planName: string
    amount: number
    credit: number
    fullPrice: number
    voucherDiscount: number
    voucherCode: string
    currency: string
    billing: 'monthly' | 'annual'
  } | null>(null)

  const [voucher, setVoucher] = useState<VoucherState>({ code: '', status: 'idle' })
  const [activePlanForVoucher, setActivePlanForVoucher] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly')
  // České formátování čísel — tisíce oddělené pevnou mezerou (5 292)
  const fmt = (n: number) => (n ?? 0).toLocaleString('cs-CZ')

  // Features ceníku skládáme z číselných limitů přes i18n (backend vrací jen české texty).
  // Fallback na plan.features, kdyby limits chyběly.
  const planFeatures = (plan: any): string[] => {
    const L = plan?.limits
    if (!L) return Array.isArray(plan?.features) ? plan.features : []
    const f: string[] = []
    f.push(t('pricing.feat_websites', { count: L.websites, defaultValue: 'Až {{count}} webů' }))
    if (L.analyses_per_day === -1) f.push(t('pricing.feat_analyses_unlimited', { defaultValue: 'Neomezené SEO analýzy' }))
    else if (L.analyses_per_day > 0) f.push(t('pricing.feat_analyses_day', { count: L.analyses_per_day, defaultValue: '{{count}} analýz denně' }))
    if (plan.id === 'free') f.push(t('pricing.feat_tools', { defaultValue: '24 SEO nástrojů' }))
    if (L.crawl_pages > 0) f.push(t('pricing.feat_crawl', { count: fmt(L.crawl_pages), defaultValue: 'Crawl až {{count}} stránek' }))
    if (L.rank_projects > 0) f.push(t('pricing.feat_rank', { projects: L.rank_projects, keywords: L.rank_keywords, defaultValue: '{{projects}} rank projektů, {{keywords}} klíčových slov' }))
    if (L.rank_keywords > 0) f.push(t('pricing.feat_rank_check', { interval: t('pricing.interval_' + (L.rank_interval || 'monthly'), { defaultValue: L.rank_interval || 'měsíčně' }), defaultValue: 'Kontrola pozic {{interval}}' }))
    if (L.ai_analysis) f.push(t('pricing.feat_ai', { defaultValue: 'AI analýza obsahu' }))
    if (L.api_access) f.push(t('pricing.feat_api', { defaultValue: 'API přístup' }))
    return f
  }
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data } = useQuery({
    queryKey: ['plans', cur],
    queryFn: () => api.get('/plans?currency=' + cur).then((r) => r.data.plans),
  })

  const checkVoucher = (code: string, planId: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!code.trim()) {
      setVoucher({ code, status: 'idle' })
      return
    }
    setVoucher(v => ({ ...v, code, status: 'checking' }))
    debounceRef.current = setTimeout(async () => {
      try {
        // Guest může voucher ověřit také — e-mail doplní až při nákupu.
        const { data: res } = await api.post('/vouchers/validate', {
          code: code.trim(), plan_id: planId, currency: cur, billing, ...(guestEmail ? { email: guestEmail } : {}),
        })
        if (res.valid) {
          setVoucher({ code, status: 'valid', discount: {
            type: res.type,
            value: res.value,
            discount_czk: res.discount_czk,
            discount_halere: res.discount_halere,
            final_price_czk: res.final_price_czk,
            planId: res.plan_id ?? null,
          }})
        } else {
          setVoucher({ code, status: 'invalid', error: res.error })
        }
      } catch {
        setVoucher(v => ({ ...v, status: 'invalid', error: t('pricing.voucher_error') }))
      }
    }, 500)
  }

  // Při přepnutí měsíční/roční přepočítej i náhled voucheru (sleva závisí na období).
  useEffect(() => {
    if (voucher.code && voucher.status === 'valid' && activePlanForVoucher) {
      checkVoucher(voucher.code, activePlanForVoucher)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billing])

  // Příchod z promo lišty (?promo=KÓD) — předvyplň a rovnou ověř voucher, ať se sleva
  // hned propíše na balíčky (preview mód /vouchers/validate plán neřeší, vrátí slevu i dotčené plány).
  useEffect(() => {
    const promo = searchParams.get('promo')
    if (promo && !voucher.code) {
      setActivePlanForVoucher('starter')
      checkVoucher(promo.toUpperCase(), 'starter')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUpgrade = async (planId: string) => {
    if (planId === 'free') return
    // Host (nepřihlášený) → otevři krok pro zadání e-mailu (auto-účet při nákupu).
    if (!isAuthenticated) {
      setGuestErr('')
      setGuestPlan(planId)
      return
    }
    await runUpgrade(planId)
  }

  const submitGuest = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!guestPlan) return
    setGuestErr('')
    setGuestLoading(true)
    try {
      await runUpgrade(guestPlan, guestEmail.trim())
      setGuestPlan(null)
    } catch (err: any) {
      const data = err.response?.data
      if (data?.needs_login) {
        setGuestPlan(null)
        openAuthModal('login')
      } else {
        setGuestErr(data?.message || t('common.error'))
      }
    } finally {
      setGuestLoading(false)
    }
  }

  const runUpgrade = async (planId: string, email?: string) => {
    // Set active plan for voucher input and trigger re-check if code exists
    setActivePlanForVoucher(planId)
    if (voucher.code && planId !== activePlanForVoucher) {
      checkVoucher(voucher.code, planId)
    }

    const voucherCode = voucher.status === 'valid' ? voucher.code : ''

    try {
      const { data: result } = await api.post('/subscription/upgrade', {
        plan: planId,
        voucher_code: voucherCode,
        billing,
        currency: cur,
        ...(email ? { email } : {}),
      })

      // Guest: nově založený účet → token → rovnou přihlásit.
      if (result.token) { await loginWithToken(result.token) }

      if (result.downgrade) {
        const planName = result.new_plan ? result.new_plan.charAt(0).toUpperCase() + result.new_plan.slice(1) : ''
        const date = result.effective_date ? new Date(result.effective_date).toLocaleDateString() : ''
        await confirm({
          title: t('pricing.downgrade_title'),
          message: t('pricing.downgrade_message', {
            plan: planName, date,
            defaultValue: 'Plán bude snížen na {{plan}} po skončení současného období ({{date}}).',
          }),
          confirmText: t('common.understood'),
          variant: 'info' as any,
        })
        return
      }

      // Zdarma — voucher pokryl celou cenu, předplatné už aktivováno
      if (result.free) {
        await loadUser?.()
        toast.success(result.message || t('pricing.activated_free', { defaultValue: 'Předplatné aktivováno zdarma' }))
        await confirm({
          title: t('pricing.activated_free_title', { defaultValue: 'Předplatné aktivováno 🎉' }),
          message: result.message,
          confirmText: t('common.understood'),
          variant: 'info' as any,
        })
        navigate('/dashboard')
        return
      }

      if (result.client_secret) {
        setPaymentData({
          clientSecret: result.client_secret,
          stripeKey: result.stripe_key,
          planName: result.plan_name,
          amount: result.amount,
          credit: result.credit || 0,
          fullPrice: result.full_price || result.amount,
          voucherDiscount: result.voucher_discount || 0,
          voucherCode,
          currency: result.currency || cur,
          billing: result.billing || billing,
        })
        return
      }

      const { data: meData } = await api.get('/auth/me')
      useAuthStore.getState().updateUser(meData.user)
      navigate('/dashboard')
    } catch (err: any) {
      // U guest flow (email) necháme chybu propadnout do submitGuest (řeší needs_login).
      if (email) throw err
      await confirm({ title: t('common.error'), message: err.response?.data?.message || 'Chyba při upgradu', confirmText: 'OK' })
    }
  }

  const handlePaymentSuccess = async () => {
    setPaymentData(null)
    setVoucher({ code: '', status: 'idle' })
    const { data: meData } = await api.get('/auth/me')
    useAuthStore.getState().updateUser(meData.user)
    navigate('/dashboard?payment=success&plan=' + (paymentData?.planName || ''))
  }

  const plans = data || []
  const voucherDiscount = voucher.status === 'valid' ? voucher.discount : null

  return (
    <div className="max-w-6xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          {t('pricing.subtitle')}
        </div>
        <h1 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">{t('pricing.title')}</h1>
        <p className="text-lg text-gray-500 max-w-xl mx-auto">{t('pricing.subtitle')}</p>
      </div>

      {/* Přepínač měsíční / roční (−10 %) */}
      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center bg-gray-100 dark:bg-white/5 rounded-full p-1">
          <button onClick={() => setBilling('monthly')}
            className={clsx('px-5 py-2 rounded-full text-sm font-semibold transition',
              billing === 'monthly' ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            {t('pricing.monthly')}
          </button>
          <button onClick={() => setBilling('annual')}
            className={clsx('px-5 py-2 rounded-full text-sm font-semibold transition inline-flex items-center gap-1.5',
              billing === 'annual' ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            {t('pricing.annual')}
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">−10 %</span>
          </button>
        </div>
      </div>

      {/* Voucher bar */}
      <div className="max-w-sm mx-auto mb-10">
        <div className="flex items-center gap-2 bg-white border border-gray-200 dark:bg-white/5 dark:border-white/10 rounded-2xl px-4 py-3 shadow-sm">
          <Tag className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder={t('pricing.voucher_placeholder')}
            value={voucher.code}
            onChange={e => {
              const code = e.target.value.toUpperCase()
              setVoucher(v => ({ ...v, code }))
              checkVoucher(code, activePlanForVoucher || 'starter')
            }}
            className="flex-1 bg-transparent text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none uppercase tracking-wider"
          />
          {voucher.status === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          {voucher.status === 'valid' && <CheckCircle className="w-4 h-4 text-emerald-500" />}
          {voucher.status === 'invalid' && <XCircle className="w-4 h-4 text-red-400" />}
        </div>
        {voucher.status === 'valid' && voucherDiscount && (
          <p className="text-center text-sm text-emerald-600 mt-2 font-medium">
            {t('pricing.discount_active')}: -{voucherDiscount.type === 'percent' ? `${voucherDiscount.value}%` : `${voucherDiscount.discount_czk} ${sym}`}
          </p>
        )}
        {voucher.status === 'invalid' && (
          <p className="text-center text-sm text-red-500 mt-2">{voucher.error}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan: any) => {
          const isPopular = plan.id === 'pro'
          const isCurrent = user?.plan === plan.id

          // Cenový model — bere v potaz měsíční/roční (−10 %) i voucher.
          const annual = billing === 'annual'
          const fullMonthly = plan.price                                   // listová cena / měsíc
          const totalBefore = annual ? Math.round(plan.price * 12 * 0.9) : plan.price
          // Voucher platí pro své plány (planId může být víc oddělených čárkou) — nebo pro všechny (null).
          const voucherPlans = voucherDiscount?.planId ? String(voucherDiscount.planId).split(',').map((s) => s.trim()) : null
          const voucherAppliesHere = !!voucherDiscount && (!voucherPlans || voucherPlans.includes(plan.id))
          const hasVoucher = voucherAppliesHere && plan.price > 0
          const pctOff = hasVoucher && voucherDiscount?.type === 'percent' ? Number(voucherDiscount.value) : 0
          const fixedOff = hasVoucher && voucherDiscount && voucherDiscount.type !== 'percent' ? Number(voucherDiscount.discount_czk) : 0
          const MIN_CHARGE = cur === 'eur' ? 1 : 15 // min. platba (Stripe): €1 / 15 Kč
          let totalAfter = hasVoucher
            ? Math.max(0, pctOff ? Math.ceil(totalBefore * (1 - pctOff / 100)) : totalBefore - fixedOff)
            : totalBefore
          if (hasVoucher) totalAfter = Math.max(MIN_CHARGE, totalAfter) // respektuj minimální platbu
          const monthlyAfter = annual ? Math.ceil(totalAfter / 12) : totalAfter
          const showStrike = annual || hasVoucher

          return (
            <div
              key={plan.id}
              className={clsx(
                'relative bg-white dark:bg-white/5 rounded-2xl p-6 flex flex-col transition-all',
                isPopular
                  ? 'border-2 border-primary-500 shadow-xl shadow-primary-500/10 scale-[1.03]'
                  : 'border border-gray-200 dark:border-white/10 shadow-sm hover:shadow-md hover:border-gray-300',
              )}
            >
              {isPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-600 to-primary-500 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1 shadow-lg shadow-primary-500/25">
                  <Star className="w-3 h-3" />
                  {t('pricing.most_popular')}
                </div>
              )}

              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{plan.name}</h3>
              <div className="mb-5">
                {plan.price === 0 ? (
                  <><span className="text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">0</span><span className="text-gray-400 text-sm ml-1">{sym}</span></>
                ) : (
                  <>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {showStrike && <span className="text-gray-400 text-sm line-through">{fmt(fullMonthly)}</span>}
                      <span className={clsx('text-4xl font-extrabold tracking-tight', hasVoucher ? 'text-emerald-600' : 'text-gray-900 dark:text-white')}>{fmt(monthlyAfter)}</span>
                      <span className="text-gray-400 text-sm">{sym}/{t('pricing.month')}</span>
                    </div>
                    {annual ? (
                      <p className="text-xs text-emerald-600 font-medium mt-1">
                        {fmt(totalAfter)} {sym} {t('pricing.per_year')}{hasVoucher ? '' : ` · ${t('pricing.save_10')}`}
                      </p>
                    ) : hasVoucher && (
                      <p className="text-xs text-emerald-600 font-medium mt-1">{t('pricing.discount_active')}</p>
                    )}
                  </>
                )}
              </div>

              <ul className="space-y-2.5 mb-8 flex-1">
                {planFeatures(plan).map((feature: string) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={isCurrent}
                className={clsx(
                  'w-full py-3 rounded-xl font-semibold transition-all text-sm',
                  isCurrent
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-white/5 dark:text-gray-500'
                    : isPopular
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white hover:shadow-lg hover:shadow-primary-500/25'
                      : plan.price === 0
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-500/10 dark:text-primary-400',
                )}
              >
                {isCurrent ? t('pricing.current_plan') : plan.price === 0 ? t('pricing.start_free') : t('pricing.select_plan')}
              </button>
            </div>
          )
        })}
      </div>

      <ConfirmDialog />

      {/* Guest checkout — krok pro zadání e-mailu (auto-účet při nákupu) */}
      {guestPlan && (
        // Backdrop wrapper: click-outside or Escape closes the guest-checkout modal
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" onClick={() => setGuestPlan(null)} onKeyDown={(e) => { if (e.key === 'Escape') setGuestPlan(null) }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          {/* onClick only stops the backdrop close from firing inside the modal; not an interactive control */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div ref={guestModalRef} role="dialog" aria-modal="true" className="relative w-full max-w-sm bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{t('checkout.guest_title')}</h3>
            <p className="text-sm text-gray-500 mb-5">{t('checkout.guest_desc')}</p>
            <form onSubmit={submitGuest} className="space-y-3">
              <input type="email" required value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)}
                placeholder={t('auth.email')}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input of the guest-checkout modal, focusing it speeds up the flow
                autoFocus
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              {guestErr && <p className="text-sm text-red-500">{guestErr}</p>}
              <button type="submit" disabled={guestLoading}
                className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:shadow-lg hover:shadow-primary-500/25 transition flex items-center justify-center gap-2 disabled:opacity-50">
                {guestLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('checkout.guest_continue')}
              </button>
            </form>
            <SocialButtons next="/pricing" />
            <p className="text-center text-xs text-gray-400 mt-4">
              {t('checkout.have_account')}{' '}
              <button onClick={() => { setGuestPlan(null); openAuthModal('login') }} className="text-primary-600 font-medium hover:underline">
                {t('nav.login')}
              </button>
            </p>
          </div>
        </div>
      )}

      {paymentData && (
        <PaymentModal
          clientSecret={paymentData.clientSecret}
          stripeKey={paymentData.stripeKey}
          planName={paymentData.planName}
          amount={paymentData.amount}
          credit={paymentData.credit}
          fullPrice={paymentData.fullPrice}
          voucherDiscount={paymentData.voucherDiscount}
          currency={paymentData.currency}
          billing={paymentData.billing}
          onSuccess={handlePaymentSuccess}
          onClose={() => setPaymentData(null)}
        />
      )}
    </div>
  )
}
